import React from 'react';
import { getUrlParam } from './util';
import esLocale from './locales/es.json';
import koLocale from './locales/ko.json';
import viLocale from './locales/vi.json';
import { useLocale } from './contexts/LocaleContext';

const tokenRe = /\{([^\}]+)\}/gi;

const locales = [
  { name: 'English', code: 'en', translations: {} },
  { name: 'Spanish', code: 'es', translations: esLocale },
  { name: 'Korean', code: 'ko', translations: koLocale },
  { name: 'Tiếng Việt', code: 'vi', translations: viLocale },
  { name: 'Arabic', code: 'ar', translations: {} },
];

let sourceStrings = [];
let translatedStrings = [];
let untranslatedStrings = [];
let undocumentedStrings = [];

let statsChangeListeners = [];

export const getLocales = () => locales;

export const onStatsChange = (callback) => {
  statsChangeListeners.push(callback);

  return () => {
    const index = statsChangeListeners.indexOf(callback);
    statsChangeListeners = [...statsChangeListeners.slice(0, index), ...statsChangeListeners.slice(index + 1)];
  };
};

export const fireStatsChange = () => {
  statsChangeListeners.forEach((callback) => {
    callback({ translatedStrings, untranslatedStrings, undocumentedStrings, translations: activeLocale.translations });
  });
};

const getLocaleTranslation = (normalizedString, activeLocale) =>
  activeLocale.translate ? activeLocale.translate(normalizedString) : activeLocale.translations[normalizedString];

const resetStats = (activeLocale) => {
  translatedStrings = [];
  untranslatedStrings = [];
  undocumentedStrings = [];

  sourceStrings.forEach((string) => {
    updateStats(string, getLocaleTranslation(string, activeLocale), true);
  });
};

const updateStats = (source, translation, silent) => {
  let strings;

  if (typeof translation === 'undefined') {
    strings = undocumentedStrings;
  } else if (translation.length === 0) {
    strings = untranslatedStrings;
  } else if (translation.length > 0) {
    strings = translatedStrings;
  }

  if (!sourceStrings.includes(source)) {
    sourceStrings.push(source);
  }

  if (!strings.includes(source)) {
    strings.push(source);

    if (!silent) {
      fireStatsChange();
    }
  }
};

const tagRe = /\<(\w+)\s?([^\>]*)\>([^\<]*)\<\/[^\>]+\>/g;
const selfClosingTagRe = /\<(\w+)\s?([^\>]*)\/\>/g;
const numberRe = /\d+/g;

const convertAttributeStringToProps = (attributes) =>
  attributes.split(' ').reduce((props, attr) => {
    const [name, value] = attr.split('=');
    return { ...props, [name]: typeof value === 'undefined' ? true : value.slice(0, value.length - 1).slice(1) };
  }, {});

const parseSelfClosingTags = (string) => {
  let parsed = [];
  let matchEnd = 0;
  let hasTags = false;
  let match;

  while ((match = selfClosingTagRe.exec(string))) {
    hasTags = true;
    const [substring, tagName, attributes] = match;
    const props = attributes === '' ? null : convertAttributeStringToProps(attributes);

    parsed.push(string.slice(matchEnd, match.index), React.createElement(tagName, props));
    matchEnd = match.index + substring.length;
  }

  return hasTags ? <>{[...parsed, string.slice(matchEnd)]}</> : string;
};

const parseTags = (string) => {
  let parsed = [];
  let matchEnd = 0;
  let hasTags = false;
  let match;

  while ((match = tagRe.exec(string))) {
    hasTags = true;
    const [substring, tagName, attributes, content] = match;
    const props = attributes === '' ? null : convertAttributeStringToProps(attributes);

    const parsedBefore = parseSelfClosingTags(string.slice(matchEnd, match.index));
    const parsedContent = parseSelfClosingTags(content);

    parsed.push(parsedBefore, React.createElement(tagName, props, parsedContent));
    matchEnd = match.index + substring.length;
  }

  return hasTags ? <>{[...parsed, parseSelfClosingTags(string.slice(matchEnd))]}</> : string;
};

const normalizeNumbers = (string) => {
  const numbers = [];

  const normalized = string.replace(numberRe, (number) => {
    numbers.push(number);
    return +number > 3 ? '3' : number;
  });

  const denormalize = (translatedString) => {
    let index = 0;

    return translatedString.replace(numberRe, () => {
      const realNumber = numbers[index];
      index++;
      return realNumber;
    });
  };

  return {
    normalized,
    denormalize,
  };
};

const checkNormalization = (string) => {
  const { normalized, denormalize } = normalizeNumbers(string);
  const denormalized = denormalize(normalized);

  if (denormalized !== string) {
    console.error({
      message: 'String is different after normalization process',
      original: string,
      normalized,
      denormalized,
    });
    throw new Error('String is different after normalization process');
  }
};

const replaceTokens = (string, tokenValues) => {
  if (!tokenValues) {
    return string;
  }

  return string.replace(tokenRe, (_, token) => {
    if (!(token in tokenValues)) {
      console.error({
        message: 'String token not found in tokenValues',
        string,
        token,
        tokenValueKeys: Object.keys(tokenValues),
      });
      throw new Error('String token not found in tokenValues');
    }

    return tokenValues[token];
  });
};

const t = (string, tokenValues) => {
  const { locale: activeLocale } = useLocale();

  if (string === '' || string === null || typeof string === 'boolean' || typeof string === 'undefined') {
    return string;
  }

  if (typeof string !== 'string') {
    console.error({ message: 'Attempted to translate non-string', string });
    throw new Error('Attempted to translate non-string');
  }

  if (activeLocale.code === 'en') {
    checkNormalization(string);
  }

  const { normalized, denormalize } = normalizeNumbers(string);
  const translated = getLocaleTranslation(normalized, activeLocale);

  updateStats(normalized, translated);

  return parseTags(replaceTokens((translated || '').length > 0 ? denormalize(translated) : string, tokenValues));
};

export default t;
